import React, { useId, useState } from "react";
import { startCase } from "lodash";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import ExtractionPointStatusTags from "@components/shared/ExtractionPointStatusTags";
import Loading from "@components/shared/Loading";
import LoadFailed, { showsOwnFailure } from "@components/zenith/ui/LoadFailed";
import Table from "@components/zenith/ui/Table";
import { useAppContext } from "@context/AppContext";
import { useAllSubscriberExtractionPoints } from "@hooks/query/zenith/useAllExtractionPoints";
import { ExtractionPointType } from "@services/extractionPoints";
import { orderOfftakes } from "@utils/orderOfftakes";
import { parseLatLng } from "@utils/parseLatLng";
import { formatSchemeDay } from "@utils/schemeDay";
import OfftakeDetailModal from "./OfftakeDetailModal";

const OfftakesAndMetersTab: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const captionId = useId();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState(
    searchParams.get("extractionPointName") ?? "",
  );
  const [selected, setSelected] = useState<any>();

  const {
    data: points = [],
    isLoading: isPointsLoading,
    isError,
  } = useZoneOfftakes();

  if (isError) return <LoadFailed />;
  if (isPointsLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  const rows = orderOfftakes(
    points.filter((point: any) =>
      point.name.toLowerCase().includes(name.toLowerCase()),
    ),
  );

  return (
    <div className="space-y-4">
      <div className="max-w-sm">
        <Label htmlFor="extractionPointName">
          {t("extraction_point.filter_name")}
        </Label>
        <SearchInput
          id="extractionPointName"
          onChange={e => setName(e.target.value)}
          value={name}
          placeholder={t("common.search") as string}
        />
      </div>

      <p id={captionId} className="text-sm text-gray-500">
        {t("zenith.zone_accounts.offtakes_caption")}
      </p>
      <Table
        aria-label={
          t("zenith.zone_accounts.tabs.offtakes_and_meters") as string
        }
        aria-describedby={captionId}
        rowKey={(point: any) => point.id}
        rows={rows}
        empty={t("extraction_point.no_extraction_point_data")}
        columns={[
          {
            key: "name",
            label: t("extraction_point.name"),
            render: (point: any) => (
              <button
                type="button"
                onClick={() => setSelected(point)}
                className="font-semibold text-primary-1 hover:underline"
              >
                #{point.name}
              </button>
            ),
          },
          {
            key: "meter",
            label: t("meter.serial_no"),
            render: (point: any) =>
              point.meter?.serialNo ?? t("meter.unmetered"),
          },
          {
            key: "location",
            label: t("zenith.zone_accounts.location"),
            render: (point: any) => {
              const { isValid, lat, lng } = parseLatLng(
                point.address?.location,
              );
              return isValid ? `${lat?.toFixed(5)}, ${lng?.toFixed(5)}` : "-";
            },
          },
          {
            key: "source",
            label: t("extraction_point.source"),
            render: (point: any) => startCase(point.source),
          },
          {
            key: "lastRead",
            label: t("zenith.zone_accounts.last_read"),
            render: (point: any) =>
              point.lastDeclaration
                ? formatSchemeDay(point.lastDeclaration.readAt)
                : "-",
          },
          {
            key: "status",
            label: t("extraction_point.is_active"),
            render: (point: any) => <ExtractionPointStatusTags point={point} />,
          },
        ]}
      />

      {selected ? (
        <OfftakeDetailModal
          point={selected}
          onClose={() => setSelected(undefined)}
        />
      ) : null}
    </div>
  );
};

/** The subscriber's regular offtakes in the page's zone, each with its last read. */
export const useZoneOfftakes = () => {
  const { loggedInInfo } = useAppContext();
  const { id: level0ResourceId = "" } = useParams();
  const walletId = loggedInInfo?.userDetails?.walletId;

  return useAllSubscriberExtractionPoints({
    params: {
      level0ResourceId,
      definedByWalletId: walletId,
      type: ExtractionPointType.Regular,
    },
    options: {
      enabled: Boolean(level0ResourceId) && Boolean(walletId),
      ...showsOwnFailure,
    },
  });
};

export default OfftakesAndMetersTab;
